/**
 * Comprehensive Interview Analysis Types
 *
 * Combines interview performance, resume analysis, and question set evaluation
 * for a complete candidate assessment.
 */

import { z } from 'zod'

/**
 * Category scores for interview analysis
 */
export const InterviewCategoryScoresSchema = z.object({
  technicalSkills: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Technical skills score from interview answers'),
  communication: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Communication score from interview transcript'),
  roleReadiness: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe(
      'Role readiness score evaluating relevant experience, skills match, and growth potential'
    ),
  culturalFit: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Cultural fit score from interview responses'),
  roleAlignment: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Role alignment combining interview + resume + job requirements'),
  experienceRelevance: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Experience relevance from resume analysis integration'),
  qualificationsMatch: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Qualifications match from resume + interview'),
})

/**
 * Resume analysis integration data
 */
export const ResumeAnalysisIntegrationSchema = z.object({
  overallScore: z.number().int().min(0).max(100).optional(),
  relevantExperienceScore: z.number().int().min(0).max(100).optional(),
  stabilityScore: z.number().int().min(0).max(100).optional(),
  qualificationsScore: z.number().int().min(0).max(100).optional(),
  roleAlignmentScore: z.number().int().min(0).max(100).optional(),
  strengths: z.array(z.string()).optional(),
  improvements: z.array(z.string()).optional(),
})

/**
 * Question set evaluation
 */
export const QuestionSetEvaluationSchema = z.object({
  totalQuestions: z.number().int(),
  answeredQuestions: z.number().int(),
  averageScore: z.number().int().min(0).max(100),
  questionScores: z
    .array(
      z.object({
        questionId: z.string(),
        questionTitle: z.string(),
        score: z.number().int().min(0).max(100),
        keyPoints: z.array(z.string()),
        concerns: z.array(z.string()).optional(),
      })
    )
    .optional(),
})

/**
 * Comprehensive Interview Analysis Schema
 */
export const ComprehensiveInterviewAnalysisSchema = z.object({
  id: z.string(),
  interviewId: z.string(),
  applicationId: z.string().optional(),
  jobId: z.string().optional(),

  // Overall assessment
  overallScore: z.number().int().min(0).max(100).describe('Overall composite score (0-100)'),
  hiringRecommendation: z.enum(['strong_hire', 'hire', 'maybe', 'not_recommend', 'strong_no_hire']),
  confidence: z
    .number()
    .int()
    .min(0)
    .max(100)
    .nullable()
    .describe('Confidence in assessment (0-100)'),

  // Category scores
  categoryScores: InterviewCategoryScoresSchema,

  // Score details (explanations per category from AI scorers)
  scoreDetails: z
    .object({
      communication: z.object({
        explanation: z.string(),
        examples: z.array(z.string()),
        fillerWordCount: z.number().optional(),
      }),
      technical: z.object({
        explanation: z.string(),
        examples: z.array(z.string()),
        techStack: z.array(z.string()).optional(),
      }),
      cultural: z.object({
        explanation: z.string(),
        examples: z.array(z.string()),
        redFlags: z.array(z.string()).optional(),
      }),
      readiness: z.object({
        explanation: z.string(),
        examples: z.array(z.string()),
        gaps: z.array(z.string()).optional(),
      }),
    })
    .optional(),

  // Resume analysis integration
  resumeAnalysis: ResumeAnalysisIntegrationSchema.optional(),

  // Question set evaluation
  questionSetEvaluation: QuestionSetEvaluationSchema.optional(),

  // Job alignment
  jobAlignment: z.object({
    requirementsMatch: z.number().int().min(0).max(100),
    skillsMatch: z.number().int().min(0).max(100),
    experienceMatch: z.number().int().min(0).max(100),
  }),

  // Insights
  strengths: z.array(z.string()).describe('Key strengths identified'),
  weaknesses: z.array(z.string()).describe('Areas for improvement'),
  recommendations: z.array(z.string()).describe('Actionable recommendations'),
  summary: z
    .string()
    .optional()
    .describe(
      'AI-generated comprehensive summary combining transcript, job description, and application analysis'
    ),

  // Comparison data
  comparison: z
    .object({
      interviewVsResume: z
        .object({
          technicalSkills: z.object({
            interview: z.number(),
            resume: z.number().optional(),
            difference: z.number(),
          }),
          communication: z.object({
            interview: z.number(),
            resume: z.number().optional(),
          }),
          overall: z.object({
            interview: z.number(),
            resume: z.number().optional(),
            difference: z.number(),
          }),
        })
        .optional(),
    })
    .optional(),

  // Metadata
  generatedAt: z.coerce.date(),
  modelVersion: z.string().optional(),
  processingTimeMs: z.number().int().optional(),
})

export type InterviewCategoryScores = z.infer<typeof InterviewCategoryScoresSchema>
export type ResumeAnalysisIntegration = z.infer<typeof ResumeAnalysisIntegrationSchema>
export type QuestionSetEvaluation = z.infer<typeof QuestionSetEvaluationSchema>
export type ComprehensiveInterviewAnalysis = z.infer<typeof ComprehensiveInterviewAnalysisSchema>
